import {
  ASSISTANT_UNAVAILABLE_MESSAGES,
  AssistantUnavailableReason,
} from '@stores/assistantAccess'

/** What the user reads for an assistantStore.errorCode. */
export const ERROR_MESSAGES: Record<string, string> = {
  RATE_LIMITED: 'You have reached the usage limit. Please try again later.',
  AI_BUSY: 'The AI service is busy. Please try again in a few minutes.',
  AI_NOT_CONFIGURED: 'The AI Assistant is not configured for this tenant.',
  AI_REQUEST_REJECTED:
    'The AI model could not process this request. Please contact your administrator.',
  OUTPUT_LIMIT:
    'The assistant ran out of room before answering. Try a narrower question.',
  FEATURE_DISABLED: 'The AI Assistant is not enabled for this tenant.',
  // Switched off by the host for every tenant: a temporary stop, not this tenant's setting.
  ASSISTANT_DISABLED:
    'The AI Assistant is temporarily turned off. Please try again later.',
  ROLE_NOT_ALLOWED: ASSISTANT_UNAVAILABLE_MESSAGES.ROLE_NOT_ALLOWED,
  PRIVACY_NOTICE_REQUIRED:
    'Please read the notice about the Assistant before you ask your first question.',
  PERMISSION_DENIED: 'You do not have permission to use the AI Assistant.',
  CONTENT_FILTERED: 'The response was blocked by the content filter.',
  MAX_ITERATIONS:
    'The assistant could not complete this request. Try rephrasing.',
  INPUT_TOO_LONG: 'The conversation is too long. Start a new chat.',
  INVALID_INPUT:
    'This message is too long or tags too many records. Shorten it and try again.',
  TIMEOUT: 'The request timed out. Please try again.',
  CONNECTION_LOST:
    'Connection interrupted. What arrived so far is shown below and may not be saved.',
  SERVER_RESTARTING: 'The service is restarting. Please retry in a moment.',
  SERVICE_UNAVAILABLE:
    'The assistant is temporarily unavailable. Please try again in a moment.',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  ACTION_FAILED: 'The action could not be completed.',
  ACTION_UNCONFIRMED:
    'We could not confirm whether this completed. Check the case or calendar before trying again.',
  CONVERSATION_BUSY:
    'This conversation is still finishing the previous answer. Please wait a moment and try again.',
  CONCURRENT_TURN_LIMIT:
    'You already have the maximum number of assistant requests running. Wait for one to finish and try again.',
  BUDGET_EXCEEDED:
    "Today's AI Assistant usage limit for your organisation has been reached. Please try again tomorrow.",
  CONVERSATION_NOT_FOUND: 'This conversation is no longer available.',
  CONVERSATION_CREATE_FAILED:
    'Could not start a conversation. Please try again.',
  CONVERSATION_LOAD_FAILED: 'Could not load this conversation.',
}

/**
 * What the composer says when voice input failed: the server's voice codes, the browser's
 * microphone errors (DOMException names) and a recording in which nothing was heard. Short, as
 * they are shown in one line under the composer.
 */
export const VOICE_ERROR_MESSAGES: Record<string, string> = {
  AI_VOICE_DISABLED: 'Voice input is turned off for your organisation.',
  AI_VOICE_INVALID_AUDIO:
    'This recording could not be transcribed. Try again, or type your question.',
  AI_VOICE_BUSY:
    'Your previous recording is still being transcribed. Try again in a moment.',
  AI_NOT_CONFIGURED: 'Voice input is not set up for your organisation.',
  AI_BUSY: 'Voice input is busy. Please try again in a moment.',
  NO_SPEECH: "Didn't catch that — try again.",
  NotAllowedError:
    'Allow microphone access in your browser to use voice input.',
  SecurityError: 'Allow microphone access in your browser to use voice input.',
  NotFoundError: 'No microphone was found.',
  OverconstrainedError: 'No microphone was found.',
  NotReadableError: 'Your microphone is in use by another app.',
  AbortError: 'Your microphone is in use by another app.',
  RECORDING_FAILED: 'The recording stopped unexpectedly. Please try again.',
  TRANSCRIBE_FAILED: 'Could not transcribe your recording. Please try again.',
}

/**
 * What the user reads for an error. The server's own message wins whenever it sent one with the
 * error: it says more than the text above (when to try again, what is wrong with the message).
 * It only ever sends text written for the user.
 */
export const errorText = (code: string, serverMessage?: string | null) =>
  serverMessage ||
  ERROR_MESSAGES[code] ||
  'Something went wrong. Please try again.'

/**
 * Refusals that say the assistant is not available to this user at all, rather than that one
 * question failed. After one of them the capabilities are read again, so that a feature
 * switched off while the page was open is shown as that (see assistantStore.reloadCapabilities).
 */
export const AVAILABILITY_ERROR_CODES: ReadonlySet<string> = new Set([
  'FEATURE_DISABLED',
  'ASSISTANT_DISABLED',
  'ROLE_NOT_ALLOWED',
  'PERMISSION_DENIED',
  'AI_NOT_CONFIGURED',
])

/**
 * The server refused a question because a case manager or specialist has not closed the privacy
 * notice in its current version (this page was loaded before the notice, or before its text
 * changed). The capabilities are read again: they carry the notice, which is then shown in place
 * of the chat (see assistantStore.dispatchTurn).
 */
export const PRIVACY_NOTICE_REQUIRED = 'PRIVACY_NOTICE_REQUIRED'

/**
 * What the user reads while the assistant is not available to them
 * (assistantStore.unavailableReason): the words of the assistant's own page for the reasons it
 * knows, the text of the code for any other.
 */
export const unavailableText = (reason: string): string => {
  const known = (texts: object) =>
    Object.prototype.hasOwnProperty.call(texts, reason)
  if (known(ASSISTANT_UNAVAILABLE_MESSAGES)) {
    return ASSISTANT_UNAVAILABLE_MESSAGES[reason as AssistantUnavailableReason]
  }
  return known(ERROR_MESSAGES)
    ? ERROR_MESSAGES[reason]
    : 'The AI Assistant is not available.'
}

/**
 * Errors that cut a turn off without the server saying how it ended, or that came from in front
 * of the server (a gateway): it may hold more of the turn than arrived here — the whole answer,
 * even. The conversation is read again shortly after, so that Retry is not offered for a
 * question that was in fact answered (see assistantStore.reconcileTurn).
 */
export const RECONCILED_ERROR_CODES: ReadonlySet<string> = new Set([
  'CONNECTION_LOST',
  'TIMEOUT',
  'SERVER_RESTARTING',
  'SERVICE_UNAVAILABLE',
])

/**
 * Errors of the turn engine itself: by the time it reports one of these it has stored the
 * question, so an answer to a clarification is on record. The answer stays on screen and is
 * sent again with Retry (which the server does not store twice) instead of being asked for a
 * second time.
 */
export const TURN_ENGINE_ERROR_CODES: ReadonlySet<string> = new Set([
  'AI_BUSY',
  'TIMEOUT',
  'SERVER_RESTARTING',
  'OUTPUT_LIMIT',
  'MAX_ITERATIONS',
])

/**
 * Errors after which the same question can simply be sent again: the turn was cut off or never
 * let in, and the server does not store a retried question twice.
 */
export const RETRYABLE_ERROR_CODES: ReadonlySet<string> = new Set([
  'CONNECTION_LOST',
  'TIMEOUT',
  'AI_BUSY',
  'SERVICE_UNAVAILABLE',
  'SERVER_RESTARTING',
  'CONVERSATION_BUSY',
  'CONCURRENT_TURN_LIMIT',
])

/**
 * Refusals the server gives before it stores anything of the message: its limits on a message,
 * the user's or the organisation's usage limits. The question is not in the conversation.
 */
const REFUSED_BEFORE_STORED_ERROR_CODES: ReadonlySet<string> = new Set([
  'INVALID_INPUT',
  'RATE_LIMITED',
  'BUDGET_EXCEEDED',
  'SESSION_EXPIRED',
  PRIVACY_NOTICE_REQUIRED,
])

/**
 * True for an error that means the question never became part of the conversation: one of the
 * refusals above, or a request the server turned down before the stream opened (HTTP 4xx). A
 * question typed in the composer goes back there instead of staying on screen as if it had been
 * asked (see assistantStore.dispatchTurn).
 */
export const isRefusedBeforeStored = (code: string): boolean =>
  REFUSED_BEFORE_STORED_ERROR_CODES.has(code) || /^HTTP_4\d\d$/.test(code)
